import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

export default function Usuarios() {
  const { user, loading } = useAuth();
  console.log('user context:', user);
  console.log('loading context:', loading);

  const [usuarios, setUsuarios] = useState([]);
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(false);
  const [usuarioAtual, setUsuarioAtual] = useState({
    nome: '',
    email: '',
    senha: '',
    role: 'usuario'
  });
  const [confirmacaoOpen, setConfirmacaoOpen] = useState(false);
  const [usuarioParaExcluir, setUsuarioParaExcluir] = useState(null);

  const carregarUsuarios = async () => {
    try {
      const response = await api.get('/usuarios');
      setUsuarios(response.data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar lista de usuários');
    }
  };

  useEffect(() => {
    carregarUsuarios();
  }, []);

  const handleClickOpen = () => {
    setUsuarioAtual({
      nome: '',
      email: '',
      senha: '',
      role: 'usuario'
    });
    setEditando(false);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleEdit = (usuario) => {
    setUsuarioAtual({
      id: usuario.id,
      nome: usuario.nome,
      email: usuario.email,
      senha: '',
      role: usuario.role
    });
    setEditando(true);
    setOpen(true);
  };

  const handleDelete = (usuario) => {
    setUsuarioParaExcluir(usuario);
    setConfirmacaoOpen(true);
  };

  const confirmarExclusao = async () => {
    try {
      await api.delete(`/usuarios/${usuarioParaExcluir.id}`);
      toast.success('Usuário excluído com sucesso');
      setConfirmacaoOpen(false);
      carregarUsuarios();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      toast.error(error.response?.data?.error || 'Erro ao excluir usuário');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUsuarioAtual({ ...usuarioAtual, [name]: value });
  };

  const handleSubmit = async () => {
    try {
      if (editando) {
        // Remover senha se estiver vazia
        const dadosAtualizados = { ...usuarioAtual };
        if (!dadosAtualizados.senha) {
          delete dadosAtualizados.senha;
        }
        await api.put(`/usuarios/${usuarioAtual.id}`, dadosAtualizados);
        toast.success('Usuário atualizado com sucesso');
      } else {
        await api.post('/usuarios', usuarioAtual);
        toast.success('Usuário criado com sucesso');
      }
      handleClose();
      carregarUsuarios();
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar usuário');
    }
  };

  if (loading) {
    console.log('AuthContext still loading');
    return <div>Carregando...</div>;
  }

  const isAdmin = user?.role === 'admin';
  console.log('isAdmin:', isAdmin, 'user?.role:', user?.role);

  if (!isAdmin) {
    console.log('Acesso negado para user:', user);
    return (
      <Container style={{ marginTop: 20 }}>
        <Typography variant="h5" gutterBottom>
          Acesso Negado
        </Typography>
        <Typography variant="body1">
          Você não tem permissão para acessar esta página. Entre em contato com um administrador.
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, pl: { xs: 1, md: 1.5 }, mt: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" gutterBottom>
          Gerenciamento de Usuários
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleClickOpen}
        >
          Novo Usuário
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Perfil</TableCell>
              <TableCell>Data de Cadastro</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usuarios.map((usuario) => (
              <TableRow key={usuario.id}>
                <TableCell>{usuario.nome}</TableCell>
                <TableCell>{usuario.email}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    {usuario.role === 'admin' ? (
                      <>
                        <AdminPanelSettingsIcon color="primary" style={{ marginRight: 8 }} />
                        Administrador
                      </>
                    ) : (
                      <>
                        <PersonIcon color="action" style={{ marginRight: 8 }} />
                        Usuário
                      </>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {format(new Date(usuario.createdAt), 'dd/MM/yyyy')}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar">
                    <IconButton onClick={() => handleEdit(usuario)} color="primary">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  {user.id !== usuario.id && (
                    <Tooltip title="Excluir">
                      <IconButton onClick={() => handleDelete(usuario)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo de Criação/Edição */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editando ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome"
            type="text"
            fullWidth
            name="nome"
            value={usuarioAtual.nome}
            onChange={handleInputChange}
            required
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            name="email"
            value={usuarioAtual.email}
            onChange={handleInputChange}
            required
          />
          <TextField
            margin="dense"
            label={editando ? 'Nova Senha (deixe em branco para manter a atual)' : 'Senha'}
            type="password"
            fullWidth
            name="senha"
            value={usuarioAtual.senha}
            onChange={handleInputChange}
            required={!editando}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Perfil</InputLabel>
            <Select
              name="role"
              value={usuarioAtual.role}
              onChange={handleInputChange}
            >
              <MenuItem value="usuario">Usuário</MenuItem>
              <MenuItem value="admin">Administrador</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Confirmação de Exclusão */}
      <Dialog open={confirmacaoOpen} onClose={() => setConfirmacaoOpen(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o usuário {usuarioParaExcluir?.nome}?
          </Typography>
          <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmacaoOpen(false)} color="inherit">
            Cancelar
          </Button>
          <Button onClick={confirmarExclusao} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
